import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Eraser, History, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { getToken } from '../../services/tokenService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/nettoyage-zero`;
const hdr = () => ({ Authorization: `Bearer ${getToken()}` });
const fmt = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
const KIND = { souscription: 'Souscription', proposition: 'Proposition', document: 'PDF GED' };
const key = (x) => `${x.kind}:${x.id}`;

const Historique = ({ items, onRestore }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="nettoyage-historique">
    <h4 className="flex items-center gap-2 font-semibold text-slate-800 mb-2"><History className="h-4 w-4" /> Historique des retraits</h4>
    {!items.length ? <p className="text-sm text-slate-500">Aucun retrait.</p> : (
      <ul className="divide-y divide-slate-100 text-sm">
        {items.map((h) => (
          <li key={h.hid} className="flex flex-wrap items-center gap-3 py-2" data-testid={`nettoyage-hist-${h.hid}`}>
            <span className="text-xs rounded-full bg-slate-100 px-2 py-0.5">{KIND[h.kind]}</span>
            <span className="font-medium">{h.membre}</span>
            <span className="text-xs text-slate-500">{h.origine} · retiré le {fmt(h.masque_at)} par {h.par}</span>
            {h.restaure_at ? <span className="ml-auto text-xs text-emerald-700">Restauré le {fmt(h.restaure_at)}</span> : (
              <button type="button" onClick={() => onRestore(h.hid)} data-testid={`nettoyage-restaurer-${h.hid}`}
                className="ml-auto inline-flex items-center gap-1 text-xs text-sky-700 hover:underline"><RotateCcw className="h-3 w-3" /> Restaurer</button>
            )}
          </li>
        ))}
      </ul>
    )}
  </div>
);

export const NettoyageZeroPanel = () => {
  const [items, setItems] = useState(null);
  const [hist, setHist] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    axios.get(API, { headers: hdr() }).then((r) => setItems(r.data.items)).catch(() => setItems([]));
    axios.get(`${API}/historique`, { headers: hdr() }).then((r) => setHist(r.data.items)).catch(() => setHist([]));
    setSel(new Set());
  }, []);
  useEffect(load, [load]);
  const toggle = (k) => setSel((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const masquer = async (liste) => {
    if (!liste.length || !window.confirm(`Retirer ${liste.length} élément(s) à 0 € des espaces membre ? Une trace est conservée dans l'historique.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/masquer`, { items: liste.map(({ kind, id }) => ({ kind, id })) }, { headers: hdr() });
      toast.success(`${r.data.masques} élément(s) retiré(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec du retrait');
    } finally {
      setBusy(false);
    }
  };
  const restaurer = async (hid) => {
    try { await axios.post(`${API}/historique/${hid}/restaurer`, {}, { headers: hdr() }); toast.success('Élément restauré'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Échec'); }
  };
  if (items === null) return <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const choisis = items.filter((x) => sel.has(key(x)));
  return (
    <div className="space-y-4" data-testid="nettoyage-zero-panel">
      <div className="rounded-xl border border-rose-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <h3 className="flex items-center gap-2 font-bold text-slate-900"><Eraser className="h-5 w-5 text-rose-600" /> Propositions, simulations et souscriptions à 0 €</h3>
          <span className="text-xs text-slate-500" data-testid="nettoyage-total">{items.length} élément(s) — souscriptions sans paiement ni signature uniquement</span>
          <Button size="sm" variant="destructive" className="ml-auto" disabled={!choisis.length || busy} onClick={() => masquer(choisis)} data-testid="nettoyage-masquer-selection">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Eraser className="h-4 w-4 mr-1" />} Retirer la sélection ({choisis.length})
          </Button>
        </div>
        {!items.length ? <p className="text-sm text-slate-500" data-testid="nettoyage-vide">Aucun élément à 0 €.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500 border-b">
              <th className="py-2 w-8"><Checkbox checked={choisis.length === items.length} onCheckedChange={(v) => setSel(v ? new Set(items.map(key)) : new Set())} data-testid="nettoyage-tout" /></th>
              <th>Type</th><th>Membre</th><th>Origine</th><th>Date</th><th>CA</th><th />
            </tr></thead>
            <tbody>
              {items.map((x) => (
                <tr key={key(x)} className="border-b border-slate-100" data-testid={`nettoyage-row-${x.id}`}>
                  <td className="py-2"><Checkbox checked={sel.has(key(x))} onCheckedChange={() => toggle(key(x))} data-testid={`nettoyage-check-${x.id}`} /></td>
                  <td><span className="text-xs rounded-full bg-rose-50 text-rose-700 px-2 py-0.5">{KIND[x.kind]}</span></td>
                  <td className="font-medium">{x.membre}</td>
                  <td className="text-xs text-slate-600">{x.origine}</td>
                  <td className="text-xs">{fmt(x.date)}</td>
                  <td className="text-xs">{x.ca ? `${Number(x.ca).toLocaleString('fr-FR')} €` : '—'}</td>
                  <td className="text-right"><button type="button" onClick={() => masquer([x])} disabled={busy} data-testid={`nettoyage-masquer-${x.id}`}
                    className="text-xs text-rose-700 hover:underline">Retirer</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <Historique items={hist} onRestore={restaurer} />
    </div>
  );
};
